import { motion } from "framer-motion";
import { Apple, ArrowRight, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { apps } from "@/data/apps";

const AppsPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const appLandingPages = {
    scribemind: "/scribemind",
    "selah-daily": "/selahdaily",
    "josh-ai": "/joshai",
  };

  return (
    <div className="min-h-screen pt-20" data-testid="apps-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <p className="caption mb-4">Our Apps</p>
            <h1 className="heading-1 mb-4">
              Apps that{" "}
              <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                work for you.
              </span>
            </h1>
            <p className="body-lg max-w-2xl mx-auto">
              Discover our collection of AI-powered applications designed to solve real problems 
              and make your life better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Apps List */}
      <section className="pb-24 bg-white">
        <div className="container-main">
          <motion.div
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            transition={{ staggerChildren: 0.08 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          >
            {apps.map((app, index) => {
              const landingPage = appLandingPages[app.id];

              return (
                <motion.article
                  key={app.id}
                  {...fadeInUp}
                  transition={{ ...fadeInUp.transition, delay: index * 0.05 }}
                  className="feature-card h-full flex flex-col"
                  data-testid={`apps-grid-card-${app.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-sm border border-zinc-100 flex-shrink-0">
                        {app.iconImage ? (
                          <img src={app.iconImage} alt={`${app.name} icon`} className="w-full h-full object-cover" />
                        ) : (
                          <Camera className="w-8 h-8 text-zinc-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        {landingPage ? (
                          <Link
                            to={landingPage}
                            className="inline-flex items-center gap-2 text-xl sm:text-2xl font-medium tracking-tight text-zinc-900 hover:text-zinc-700 transition-colors"
                            data-testid={`apps-landing-link-${app.id}`}
                          >
                            <span className="truncate">{app.name}</span>
                            <ArrowRight className="w-4 h-4 flex-shrink-0" />
                          </Link>
                        ) : (
                          <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-900">
                            {app.name}
                          </h2>
                        )}
                        <p className="text-sm text-zinc-400 mt-1">{app.tagline}</p>
                      </div>
                    </div>
                    <p className="hidden lg:block text-xs uppercase tracking-[0.2em] text-zinc-300 text-right">
                      {String(index + 1).padStart(2, "0")}
                    </p>
                  </div>

                  <p className="body-text mb-8">{app.shortDescription}</p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {app.features.slice(0, 3).map((feature) => (
                      <span
                        key={feature}
                        className="rounded-full bg-zinc-100 px-3 py-2 text-xs sm:text-sm text-zinc-600"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto flex flex-wrap items-center gap-3">
                    {app.appStoreUrl ? (
                      <a
                        href={app.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center justify-center gap-2"
                        data-testid={`apps-ios-link-${app.id}`}
                      >
                        <Apple className="w-4 h-4" />
                        Download on iOS
                      </a>
                    ) : (
                      <span className="h-12 px-6 rounded-full bg-zinc-100 text-zinc-500 font-medium text-sm inline-flex items-center justify-center">
                        Coming soon
                      </span>
                    )}

                    {landingPage && (
                      <Link
                        to={landingPage}
                        className="btn-secondary inline-flex items-center justify-center"
                        data-testid={`apps-learn-more-${app.id}`}
                      >
                        Learn More
                      </Link>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <h2 className="heading-3 mb-4">Have an idea for an app?</h2>
            <p className="body-text max-w-xl mx-auto mb-8">
              We're always looking for new ways to help people with practical AI solutions. 
              Share your ideas with us.
            </p>
            <a
              href="/contact"
              className="btn-primary inline-flex items-center justify-center"
              data-testid="apps-contact-btn"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AppsPage;
